import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  ExpensePoliciesDocument,
  ExpensePoliciesQuery,
  useExpensePoliciesQuery,
  useUpsertPolicyMutation,
} from "@common/utils/graphql";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import { ExpensePolicyForm } from "./ExpensePolicyForm";
import { ExtractTypename } from "@common/utils/graphql/utils";
import { DataTable } from "@common/components/Table/DataTable";
import { policyTableColumns } from "./PolicyTable";
import { Card } from "@/components/ui/card";
import { NoResultsCard } from "@common/components";
import { FileDashed } from "@phosphor-icons/react";

export type Policy = ExtractTypename<
  ExpensePoliciesQuery["expensePolicies"],
  "QueryExpensePoliciesSuccess"
>["data"]["policies"]["0"];

export const ExpensePolicy = () => {
  const [formIsOpen, setFormIsOpen] = useState(false);
  const [policy, setPolicy] = useState<Policy>();

  const { data, loading } = useExpensePoliciesQuery();

  const [upsertDefaultPolicy, { loading: isDeleting }] =
    useUpsertPolicyMutation({
      refetchQueries: [ExpensePoliciesDocument],
    });

  const closeForm = () => {
    setPolicy(undefined);
    setFormIsOpen(false);
  };

  const editPolicy = (policy: Policy) => {
    setPolicy(policy);
    setFormIsOpen(true);
  };

  const removePolicy = ({ policy, name, uuid, departmentIds }: Policy) => {
    upsertDefaultPolicy({
      variables: {
        policy,
        name,
        uuid,
        departmentIds,
        isActive: false,
      },
    });
  };

  const policies =
    data?.expensePolicies?.__typename === "QueryExpensePoliciesSuccess"
      ? data.expensePolicies.data.policies.filter((policy) => policy.isActive)
      : [];

  if (loading && policies.length === 0) {
    return <FullPageLoader />;
  }

  const columns = policyTableColumns(editPolicy, removePolicy, isDeleting);

  return (
    <div className="mt-6 flex  h-full flex-col">
      <div className="flex w-full justify-end">
        <Button onClick={() => setFormIsOpen(true)}>Create New Policy</Button>
      </div>
      {policies.length === 0 ? (
        <NoResultsCard
          Icon={FileDashed}
          header="No Active Policies"
          body="Create a new policy to get started."
        />
      ) : (
        <Card className="mt-6">
          <DataTable columns={columns} data={policies} />
        </Card>
      )}
      {formIsOpen && (
        <ExpensePolicyForm
          policies={policies}
          onClose={closeForm}
          editPolicy={policy}
        />
      )}
    </div>
  );
};
